import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import { setUser } from '../store/slices/authSlice';
import { useTranslation } from '../hooks/useTranslation';
import { Layout } from '../components/layout/Layout';
import { signIn } from '../services/auth';
import { auth } from '../services/firebase';
import { RootState } from '../store/store';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

const LoginContainer = styled.div`
  max-width: 400px;
  margin: 60px auto;
  padding: var(--spacing-6);
  background: var(--color-background-secondary);
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-6);
  text-align: center;
  font-size: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
`;

const Input = styled.input`
  padding: var(--spacing-3);
  background: var(--color-background-input);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  color: var(--color-text-primary);
  font-size: 1rem;
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }

  &::placeholder {
    color: var(--color-text-secondary);
  }
`;

const Button = styled.button`
  padding: var(--spacing-3);
  background: var(--color-primary);
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;

  &:hover {
    background: var(--color-primary-dark);
  }

  &:disabled {
    background: var(--color-primary-light);
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const RegisterLink = styled.div`
  text-align: center;
  margin-top: var(--spacing-4);
  color: var(--color-text-primary);
  font-size: 0.9rem;

  a {
    color: var(--color-primary);
    text-decoration: none;
    margin-left: 0.5rem;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  color: var(--color-error);
  text-align: center;
  padding: var(--spacing-2);
  background: var(--color-error-bg);
  border-radius: 4px;
  margin-top: var(--spacing-2);
`;

const Login = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from?.pathname || '/';
  const authState = useSelector((state: RootState) => state.auth);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true); // Commencer avec loading=true pour vérifier l'état d'authentification
  const [error, setError] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Vérifier si l'utilisateur est déjà connecté
  useEffect(() => {
    const checkAuthState = () => {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        if (user) {
          console.log('Utilisateur déjà connecté, redirection...');
          navigate(from, { replace: true });
        } else {
          setCheckingAuth(false);
          setLoading(false);
        }
      });

      return unsubscribe;
    };

    const unsubscribe = checkAuthState();
    return () => unsubscribe();
  }, [navigate, from]);

  // Vérifier également si l'utilisateur est déjà dans le state Redux
  useEffect(() => {
    if (authState.user) {
      console.log('Utilisateur trouvé dans Redux state, redirection...');
      navigate(from, { replace: true });
    }
  }, [authState.user, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Persister explicitement l'authentification
      const user = await signIn(email, password);
      dispatch(setUser(user));
      
      // Stocker une information supplémentaire dans localStorage pour vérifier la persistance
      localStorage.setItem('cinedelux_auth_check', 'true');
      
      navigate(from, { replace: true });
    } catch (err: any) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Email ou mot de passe incorrect');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Trop de tentatives, veuillez réessayer plus tard');
      } else {
        setError('Une erreur est survenue, veuillez réessayer');
      }
      console.error('Error signing in:', err);
    } finally {
      setLoading(false);
    }
  };

  // Afficher un indicateur de chargement pendant la vérification de l'authentification
  if (checkingAuth) {
    return (
      <Layout>
        <div className="flex justify-center items-center" style={{ minHeight: '60vh' }}>
          <LoadingSpinner />
          <p className="ml-3">Vérification de l'authentification...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <LoginContainer>
        <Title>Connexion</Title>
        <Form onSubmit={handleSubmit}>
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <Input
            type="password"
            placeholder="Mot de passe"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
          <Button type="submit" disabled={loading}>
            {loading ? "Connexion en cours..." : "Se connecter"}
          </Button>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Form>
        <RegisterLink>
          Pas encore de compte ? <Link to="/register">S'inscrire</Link>
        </RegisterLink>
      </LoginContainer>
    </Layout>
  );
};

export default Login;
